import {
  faCheckCircle as fasCheckCircle,
  faTimesCircle as fasTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import ReactGA from "react-ga";
import Modal from "react-modal";
import MaskedInput from "react-text-mask";
import { phoneNumberMask, sanitizePhoneNumber } from "utils/strings";
import * as Yup from "yup";

export interface FormFields {
  nome: string;
  mensagem?: string;
  email?: string;
  telefone?: string;
  _formsubmit_id?: string;
}

const contactFormSchema = Yup.object().shape({
  nome: Yup.string().required("Nome obrigatório"),
  email: Yup.string()
    .email("E-mail inválido")
    .when("telefone", {
      is: (telefone) => !telefone || telefone.length === 0,
      then: Yup.string().required("Email ou Telefone obrigatório"),
    }),
  telefone: Yup.string().test("telefone", "Telefone inválido", (value) => {
    const sanitized = sanitizePhoneNumber(value || "");
    return !sanitized || sanitized.length >= 10;
  }),
  mensagem: Yup.string().required("Mensagem Obrigatória"),
});

export const FormspreeForm = () => {
  const onSubmit = async (values: FormFields) => {
    try {
      const { nome, mensagem, email, telefone } = values;
      const formData = { nome, mensagem, email, telefone };
      const SES_URL: string = process.env.REACT_APP_SES_URL || "";

      setStatus("sending");
      await axios.post(SES_URL, formData, {
        maxRedirects: 0,
        validateStatus: function (status) {
          return status >= 200 && status <= 302;
        },
      });
      setStatus("success");
    } catch (error) {
      // TODO: Tratar resposta 302 do Axios como não erro.
      setStatus("success");
    }
  };

  // Para desabilitar botão de envio.
  const [sending, setSending] = useState(false);
  // Para definir resultado do modal.
  const [status, setStatus] = useState("initial");
  // Para controle do estado do modal.
  const [modalIsOpen, setIsOpen] = useState(false);
  // Para reiniciar o estado do formulário.
  const [resetter, setResetter] = useState<() => void>();

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  // Configuração inicial do modal.
  useEffect(() => Modal.setAppElement("#root"), []);

  // Trata o estado do envio.
  useEffect(() => {
    if (status && status !== "initial") {
      if (status === "sending") {
        setSending(true);
      } else {
        setSending(false);
      }

      if (status === "success") {
        openModal();
        if (resetter) {
          // Limpa o formulário quando o envio é bem-sucedido.
          resetter();
        }
        ReactGA.event({
          category: "fluxo",
          action: "cadastro",
          label: "successo",
        });
      } else if (status === "error") {
        openModal();
        ReactGA.event({
          category: "fluxo",
          action: "cadastro",
          label: "erro",
        });
      }
    }
  }, [status, resetter, sending]);

  return (
    <>
      <Formik
        initialValues={{
          email: "",
          nome: "",
          mensagem: "",
          telefone: "",
          honeypot: "",
        }}
        validationSchema={contactFormSchema}
        onSubmit={(values, { resetForm }) => {
          setResetter(() => resetForm);
          const { email, nome, telefone, mensagem, honeypot } = values;
          onSubmit({
            nome,
            ...(email ? { email } : {}),
            ...(telefone ? { telefone } : {}),
            ...(mensagem ? { mensagem } : {}),
            ...(honeypot ? { _formsubmt_id: honeypot } : {}),
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isValid,
          dirty,
        }) => (
          <form className={"contact-form"} onSubmit={handleSubmit}>
            <div className="form-group">
              <label
                className={"text-primary font-weight-bold"}
                htmlFor="input-nome"
              >
                Nome
              </label>
              <input
                type="text"
                name="nome"
                className={`form-control ${
                  errors.nome && touched.nome && errors.nome ? "is-invalid" : ""
                }`}
                id="input-nome"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.nome}
              />
            </div>
            <div className="form-group">
              <label
                className={"text-primary font-weight-bold"}
                htmlFor="input-telefone"
              >
                Telefone
              </label>
              <MaskedInput
                name="telefone"
                mask={phoneNumberMask}
                id="input-telefone"
                aria-describedby="telefone"
                className={`form-control ${
                  errors.telefone && touched.telefone && errors.telefone
                    ? "is-invalid"
                    : ""
                }`}
                type="text"
                guide={false}
                placeholder="(99) 99999-9999"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.telefone}
              />
            </div>
            <div className="form-group">
              <label
                className={"text-primary font-weight-bold"}
                htmlFor="input-email"
              >
                Email
              </label>
              <input
                type="text"
                name="email"
                className={`form-control ${
                  errors.email && touched.email && errors.email
                    ? "is-invalid"
                    : ""
                }`}
                id="input-email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
              />
            </div>
            <div className="form-group">
              <label className={"text-primary font-weight-bold"} htmlFor="">
                Mensagem
              </label>
              <textarea
                name="mensagem"
                className={`form-control ${
                  errors.mensagem && touched.mensagem && errors.mensagem
                    ? "is-invalid"
                    : ""
                }`}
                id="input-mensagem"
                aria-describedby="mensagem"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.mensagem}
              />
            </div>
            <input
              style={{ display: "none" }}
              type="text"
              name="_formsubmit_id"
              id="honeypot"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.honeypot}
            />
            <div className={"d-flex justify-content-center pb-4 "}>
              <button
                className="btn btn-footer text-light btn-lg font-weight-bold mt-4"
                type="submit"
                disabled={sending}
              >
                Enviar
              </button>
            </div>
          </form>
        )}
      </Formik>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Cadastro"
        className="modal-dialog"
        closeTimeoutMS={500}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              {status === "success"
                ? "Cadastro realizado com sucesso!"
                : "Ocorreu um erro!"}
            </h4>
            <button type="button" className="close" onClick={closeModal}>
              <span aria-hidden="true">&times;</span>
              <span className="sr-only">Fechar</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="my-5 text-center">
              {status === "success" || status === "already" ? (
                <FontAwesomeIcon
                  color="#0cb7b7"
                  icon={fasCheckCircle}
                  size="5x"
                />
              ) : status === "error" ? (
                <FontAwesomeIcon
                  color="#dc3545"
                  icon={fasTimesCircle}
                  size="5x"
                />
              ) : null}
            </div>
            <p>
              {status === "success"
                ? "Mensagem enviada com sucesso"
                : "Ocorreu um erro ao enviar sua mensagem, por favor tente novamente mais tarde."}
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className={`btn ${
                status !== "error" ? "btn-primary" : "btn-danger"
              }`}
              onClick={closeModal}
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
